/**
 * Bundle Builder Frontend Logic
 */

document.addEventListener('DOMContentLoaded', () => {

    // Formatting currency
    const formatMoney = (cents) => {
        return (cents / 100).toLocaleString('en-IN', { style: 'currency', currency: 'INR' }).replace('.00', '');
    };

    // Helper to generate unique ID
    const generateUUID = () => {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    };

    // -------------------------------------------------------------
    // Dynamic Age-Range Parsing, Natural Sorting & Size Matcher
    // -------------------------------------------------------------
    const parseAgeRange = (sizeStr) => {
        if (!sizeStr || typeof sizeStr !== 'string') return null;
        const cleanStr = sizeStr.trim();
        const isMonth = /month|m\b|\bm/i.test(cleanStr) && !/year|yr|y\b/i.test(cleanStr);
        
        // Match numbers
        const matches = cleanStr.match(/\d+(\.\d+)?/g);
        if (!matches || matches.length === 0) {
            return null; // Non-age sizes like Free Size, S, M, etc.
        }
        
        let start = parseFloat(matches[0]);
        let end = matches.length > 1 ? parseFloat(matches[1]) : start;
        
        if (start > end) {
            const temp = start;
            start = end;
            end = temp;
        }
        
        // Convert months to years for uniform numerical comparison
        const unitFactor = isMonth ? (1 / 12) : 1;
        return {
            start: start * unitFactor,
            end: end * unitFactor,
            rawStart: start,
            rawEnd: end,
            isMonth: isMonth
        };
    };

    const sortSizes = (sizes) => {
        return Array.from(sizes).sort((a, b) => {
            const rangeA = parseAgeRange(a);
            const rangeB = parseAgeRange(b);

            if (rangeA && rangeB) {
                if (Math.abs(rangeA.start - rangeB.start) > 0.001) {
                    return rangeA.start - rangeB.start;
                }
                if (Math.abs(rangeA.end - rangeB.end) > 0.001) {
                    return rangeA.end - rangeB.end;
                }
            } else if (rangeA) {
                return -1;
            } else if (rangeB) {
                return 1;
            }
            return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        });
    };

    const calculateOverlap = (rangeA, rangeB) => {
        if (!rangeA || !rangeB) return 0;
        const overlapStart = Math.max(rangeA.start, rangeB.start);
        const overlapEnd = Math.min(rangeA.end, rangeB.end);
        const overlap = overlapEnd - overlapStart;
        return overlap > 0 ? overlap : 0;
    };

    const findMatchingVariant = (variants, targetSize) => {
        if (!variants || variants.length === 0 || !targetSize) return null;
        
        const normalizedTarget = targetSize.trim().toLowerCase();
        
        // 1. Exact match on available variant
        const exactMatch = variants.find(v => v.available && v.size && v.size.trim().toLowerCase() === normalizedTarget);
        if (exactMatch) return exactMatch;
        
        const targetRange = parseAgeRange(targetSize);
        if (!targetRange) return null;
        
        let bestVariant = null;
        let highestScore = 0;
        
        variants.forEach(v => {
            if (!v.available || !v.size) return;
            
            const vRange = parseAgeRange(v.size);
            if (!vRange) return;
            
            const overlap = calculateOverlap(targetRange, vRange);
            if (overlap > 0) {
                const maxSpan = Math.max(targetRange.end - targetRange.start, vRange.end - vRange.start, 0.1);
                let score = overlap / maxSpan;
                
                // Bonus if start age matches exactly
                if (Math.abs(targetRange.start - vRange.start) < 0.001) {
                    score += 0.1;
                }
                
                if (score > highestScore) {
                    highestScore = score;
                    bestVariant = v;
                }
            }
        });
        
        return bestVariant;
    };

    // -------------------------------------------------------------
    // Readymade Bundles Carousel / Navigation Arrows & Dots
    // -------------------------------------------------------------
    const rmWrapper = document.querySelector('.bb-readymade-wrapper');
    if (rmWrapper) {
        const carousel = rmWrapper.querySelector('.js-bb-readymade-carousel');
        const prevBtn = rmWrapper.querySelector('.js-bb-prev');
        const nextBtn = rmWrapper.querySelector('.js-bb-next');
        const dotsContainer = rmWrapper.querySelector('.js-bb-carousel-dots');
        const bundles = rmWrapper.querySelectorAll('.js-readymade-bundle');

        if (carousel && prevBtn && nextBtn) {
            const updateArrows = () => {
                const scrollLeft = carousel.scrollLeft;
                const maxScroll = carousel.scrollWidth - carousel.clientWidth;

                if (carousel.scrollWidth <= carousel.clientWidth + 2) {
                    prevBtn.style.display = 'none';
                    nextBtn.style.display = 'none';
                    carousel.style.justifyContent = 'center';
                } else {
                    const isDesktopOrTablet = window.innerWidth >= 768;
                    prevBtn.style.display = isDesktopOrTablet ? 'flex' : 'none';
                    nextBtn.style.display = isDesktopOrTablet ? 'flex' : 'none';
                    carousel.style.justifyContent = 'flex-start';

                    prevBtn.style.opacity = scrollLeft <= 10 ? '0.3' : '1';
                    nextBtn.style.opacity = scrollLeft >= maxScroll - 10 ? '0.3' : '1';
                }
            };

            prevBtn.addEventListener('click', () => {
                const card = carousel.querySelector('.bb-bundle-card');
                const cardWidth = card ? card.getBoundingClientRect().width : carousel.clientWidth;
                carousel.scrollBy({ left: -(cardWidth + 24), behavior: 'smooth' });
            });

            nextBtn.addEventListener('click', () => {
                const card = carousel.querySelector('.bb-bundle-card');
                const cardWidth = card ? card.getBoundingClientRect().width : carousel.clientWidth;
                carousel.scrollBy({ left: cardWidth + 24, behavior: 'smooth' });
            });

            carousel.addEventListener('scroll', updateArrows);
            window.addEventListener('resize', updateArrows);
            // Initial state check
            setTimeout(updateArrows, 200);
        }

        // Dynamically create and manage Dots (for mobile carousel layout)
        if (carousel && dotsContainer && bundles.length > 0) {
            const setupDots = () => {
                dotsContainer.innerHTML = '';
                
                // Hide dots if all bundles fit without scrolling
                if (carousel.scrollWidth <= carousel.clientWidth + 2) {
                    dotsContainer.style.display = 'none';
                    return;
                }
                
                dotsContainer.style.display = '';
                
                bundles.forEach((_, idx) => {
                    const dot = document.createElement('button');
                    dot.type = 'button';
                    dot.className = `bb-carousel-dot${idx === 0 ? ' active' : ''}`;
                    dot.setAttribute('aria-label', `Go to slide ${idx + 1}`);
                    dot.addEventListener('click', () => {
                        const cardWidth = bundles[0].getBoundingClientRect().width + 24;
                        carousel.scrollTo({
                            left: idx * cardWidth,
                            behavior: 'smooth'
                        });
                    });
                    dotsContainer.appendChild(dot);
                });
            };

            setupDots();

            const updateActiveDot = () => {
                const cardWidth = bundles[0].getBoundingClientRect().width + 24;
                if (cardWidth <= 24) return;
                const activeIndex = Math.round(carousel.scrollLeft / cardWidth);
                const dots = dotsContainer.querySelectorAll('.bb-carousel-dot');
                dots.forEach((dot, idx) => {
                    if (idx === activeIndex) {
                        dot.classList.add('active');
                    } else {
                        dot.classList.remove('active');
                    }
                });
            };

            carousel.addEventListener('scroll', updateActiveDot);
            window.addEventListener('resize', () => {
                setupDots();
                updateActiveDot();
            });
        }
    }

    // -------------------------------------------------------------
    // Custom Bundle Builder Collection Carousels
    // -------------------------------------------------------------
    const customContainers = document.querySelectorAll('.js-cb-collection-container');
    customContainers.forEach(container => {
        const carousel = container.querySelector('.js-cb-carousel');
        const prevBtn = container.querySelector('.js-cb-prev');
        const nextBtn = container.querySelector('.js-cb-next');
        const dotsContainer = container.querySelector('.js-cb-carousel-dots');

        if (carousel && prevBtn && nextBtn && dotsContainer) {
            const setupDots = () => {
                dotsContainer.innerHTML = '';
                const visibleCards = Array.from(container.querySelectorAll('.js-cb-product-card')).filter(c => c.style.display !== 'none');
                
                if (carousel.scrollWidth <= carousel.clientWidth + 2 || visibleCards.length <= 1) {
                    dotsContainer.style.display = 'none';
                    return;
                }
                
                dotsContainer.style.display = '';
                
                visibleCards.forEach((_, idx) => {
                    const dot = document.createElement('button');
                    dot.type = 'button';
                    dot.className = `bb-carousel-dot${idx === 0 ? ' active' : ''}`;
                    dot.setAttribute('aria-label', `Go to slide ${idx + 1}`);
                    dot.addEventListener('click', () => {
                        const cardWidth = visibleCards[0].getBoundingClientRect().width + 16;
                        carousel.scrollTo({
                            left: idx * cardWidth,
                            behavior: 'smooth'
                        });
                    });
                    dotsContainer.appendChild(dot);
                });
            };

            const updateActiveDot = () => {
                const visibleCards = Array.from(container.querySelectorAll('.js-cb-product-card')).filter(c => c.style.display !== 'none');
                if (visibleCards.length === 0) return;
                
                const cardWidth = visibleCards[0].getBoundingClientRect().width + 16;
                if (cardWidth <= 16) return;
                
                const activeIndex = Math.round(carousel.scrollLeft / cardWidth);
                const dots = dotsContainer.querySelectorAll('.bb-carousel-dot');
                dots.forEach((dot, idx) => {
                    if (idx === activeIndex) {
                        dot.classList.add('active');
                    } else {
                        dot.classList.remove('active');
                    }
                });
            };

            const updateArrows = () => {
                const scrollLeft = carousel.scrollLeft;
                const maxScroll = carousel.scrollWidth - carousel.clientWidth;

                if (carousel.scrollWidth <= carousel.clientWidth + 2) {
                    prevBtn.style.display = 'none';
                    nextBtn.style.display = 'none';
                    carousel.style.justifyContent = 'center';
                } else {
                    const isDesktopOrTablet = window.innerWidth >= 768;
                    prevBtn.style.display = isDesktopOrTablet ? 'flex' : 'none';
                    nextBtn.style.display = isDesktopOrTablet ? 'flex' : 'none';
                    carousel.style.justifyContent = 'flex-start';

                    prevBtn.style.opacity = scrollLeft <= 10 ? '0.3' : '1';
                    nextBtn.style.opacity = scrollLeft >= maxScroll - 10 ? '0.3' : '1';
                }
            };

            prevBtn.addEventListener('click', () => {
                const card = carousel.querySelector('.js-cb-product-card');
                const cardWidth = card ? card.getBoundingClientRect().width : 160;
                carousel.scrollBy({ left: -(cardWidth + 16), behavior: 'smooth' });
            });

            nextBtn.addEventListener('click', () => {
                const card = carousel.querySelector('.js-cb-product-card');
                const cardWidth = card ? card.getBoundingClientRect().width : 160;
                carousel.scrollBy({ left: cardWidth + 16, behavior: 'smooth' });
            });

            carousel.addEventListener('scroll', () => {
                updateArrows();
                updateActiveDot();
            });
            window.addEventListener('resize', () => {
                setupDots();
                updateArrows();
                updateActiveDot();
            });
            
            container.addEventListener('cb-rebuild-dots', () => {
                setupDots();
                updateArrows();
                updateActiveDot();
            });

            setTimeout(() => {
                setupDots();
                updateArrows();
                updateActiveDot();
            }, 200);
        }
    });

    // -------------------------------------------------------------
    // Readymade Bundles Logic
    // -------------------------------------------------------------
    const readymadeBundles = document.querySelectorAll('.js-readymade-bundle');

    readymadeBundles.forEach(bundle => {
        const products = bundle.querySelectorAll('.js-rm-product');
        const selectEl = bundle.querySelector('.js-rm-size-select');
        const btn = bundle.querySelector('.js-rm-add-btn');
        const priceEl = bundle.querySelector('.js-rm-total-price');
        const statusMsg = bundle.querySelector('.js-rm-status-msg');

        const bundleIdStr = bundle.dataset.bundleId;
        const bundleTitle = bundle.dataset.bundleTitle;
        const discountCode = bundle.dataset.discountCode;

        let parsedProducts = [];
        let allAvailableSizes = new Set();

        products.forEach(p => {
            const jsonEl = p.querySelector('.js-rm-variants-json');
            if (jsonEl) {
                const variants = JSON.parse(jsonEl.textContent);
                parsedProducts.push({
                    el: p,
                    handle: p.dataset.productHandle,
                    variants: variants
                });

                variants.forEach(v => {
                    if (v.available && v.size) {
                        allAvailableSizes.add(v.size);
                    }
                });
            }
        });

        // Populate size dropdown
        let sizesArray = sortSizes(allAvailableSizes);
        // Filter sizes: only keep those that have an available matching variant in ALL products of the bundle
        sizesArray = sizesArray.filter(size => {
            return parsedProducts.every(p => {
                const variant = findMatchingVariant(p.variants, size);
                return variant && variant.available;
            });
        });

        sizesArray.forEach(size => {
            const opt = document.createElement('option');
            opt.value = size;
            opt.textContent = size;
            selectEl.appendChild(opt);
        });

        selectEl.addEventListener('change', () => {
            const selectedSize = selectEl.value;
            let allAvailable = true;
            let totalPrice = 0;
            let selectedVariants = [];

            parsedProducts.forEach(p => {
                const variant = findMatchingVariant(p.variants, selectedSize);
                if (!variant || !variant.available) {
                    allAvailable = false;
                } else {
                    totalPrice += variant.price;
                    selectedVariants.push(variant.id);
                }
            });

            if (allAvailable && parsedProducts.length > 0) {
                btn.disabled = false;
                statusMsg.style.display = 'none';
                priceEl.textContent = formatMoney(totalPrice);
                btn.dataset.variants = selectedVariants.join(',');
                btn.dataset.size = selectedSize;
            } else {
                btn.disabled = true;
                statusMsg.style.display = 'block';
                statusMsg.textContent = "Some products are not available in this size.";
                priceEl.textContent = '...';
            }
        });

        btn.addEventListener('click', async () => {
            if (btn.disabled) return;
            const variants = btn.dataset.variants.split(',');
            const size = btn.dataset.size;
            const uniqueBundleId = 'RM-' + generateUUID();

            const items = variants.map(vid => ({
                id: parseInt(vid),
                quantity: 1,
                properties: {
                    '_bundle_id': uniqueBundleId,
                    '_bundle_title': bundleTitle,
                    '_bundle_type': 'readymade',
                    '_bundle_discount_code': discountCode,
                    '_bundle_size': size,
                    '_bundle_item_count': variants.length.toString()
                }
            }));

            btn.disabled = true;
            const origText = btn.innerHTML;
            btn.innerHTML = 'Adding...';

            try {
                const res = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items })
                });

                if (res.ok) {
                    btn.innerHTML = 'Added!';
                    setTimeout(() => { btn.innerHTML = origText; btn.disabled = false; }, 2000);

                    if (discountCode && window.applyDiscount) {
                        await window.applyDiscount(discountCode);
                    }
                    if (window.openCartDrawer) {
                        window.openCartDrawer();
                    }
                } else {
                    const err = await res.json();
                    alert(err.description || 'Error adding to cart');
                    btn.innerHTML = origText;
                    btn.disabled = false;
                }
            } catch (e) {
                console.error(e);
                btn.innerHTML = origText;
                btn.disabled = false;
            }
        });
    });

    // -------------------------------------------------------------
    // Custom Bundle Builder
    // -------------------------------------------------------------
    const cbApp = document.getElementById('CustomBundleApp');
    if (!cbApp) return;

    const cbMin = parseInt(cbApp.dataset.min) || 2;
    const cbMax = parseInt(cbApp.dataset.max) || 5;
    const cbDiscount = cbApp.dataset.discount;
    const cbTitle = cbApp.dataset.title;

    const cbSizePillsContainer = cbApp.querySelector('.js-cb-size-pills');
    const cbStep2 = cbApp.querySelector('.js-cb-step-2');
    const cbProducts = cbApp.querySelectorAll('.js-cb-product-card');

    const stickySummary = document.querySelector('.js-cb-sticky-summary');
    const cbCountEl = document.querySelector('.js-cb-selected-count');
    const cbSizeTextEl = document.querySelector('.js-cb-selected-size-text');
    const cbTotalPriceEl = document.querySelector('.js-cb-total-price');
    const cbAddBtn = document.querySelector('.js-cb-add-btn');

    let cbParsedProducts = [];
    let cbAllSizes = new Set();
    let cbSelectedItems = []; // Array of variant objects
    let cbCurrentSize = '';

    cbProducts.forEach(p => {
        const jsonEl = p.querySelector('.js-cb-variants-json');
        if (jsonEl) {
            const variants = JSON.parse(jsonEl.textContent);
            cbParsedProducts.push({
                el: p,
                handle: p.dataset.productHandle,
                variants: variants
            });
            variants.forEach(v => {
                if (v.available && v.size) cbAllSizes.add(v.size);
            });
        }
    });

    // Populate Size Pills with Natural Chronological Age Order
    const cbSizesArray = sortSizes(cbAllSizes);
    cbSizesArray.forEach(size => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = size;
        btn.className = 'cb-size-pill';
        btn.style.padding = '10px 24px';
        btn.style.borderRadius = '50px';
        btn.style.border = '1.5px solid white';
        btn.style.background = 'transparent';
        btn.style.color = 'white';
        btn.style.cursor = 'pointer';
        btn.style.fontWeight = 'bold';
        btn.style.transition = 'all 0.2s';

        btn.addEventListener('click', () => {
            Array.from(cbSizePillsContainer.children).forEach(c => {
                c.style.background = 'transparent';
                c.style.color = 'white';
                c.style.borderColor = 'white';
            });
            btn.style.background = 'white';
            btn.style.color = '#4a2e1b';
            btn.style.borderColor = 'white';

            cbCurrentSize = size;
            cbStep2.style.opacity = '1';
            cbStep2.style.pointerEvents = 'auto';
            updateProductAvailability(cbCurrentSize);
            updateCustomSummary();

            cbStep2.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });

        cbSizePillsContainer.appendChild(btn);
    });

    const updateCustomSummary = () => {
        cbCountEl.textContent = cbSelectedItems.length;
        const total = cbSelectedItems.reduce((sum, item) => sum + item.price, 0);
        cbTotalPriceEl.textContent = formatMoney(total);

        if (cbSelectedItems.length >= cbMin && cbSelectedItems.length <= cbMax) {
            cbAddBtn.disabled = false;
        } else {
            cbAddBtn.disabled = true;
        }

        if (cbCurrentSize) {
            stickySummary.classList.add('visible');
            cbSizeTextEl.textContent = cbCurrentSize;
        } else {
            stickySummary.classList.remove('visible');
        }
    };

    const updateProductAvailability = (size) => {
        cbParsedProducts.forEach(p => {
            const variant = findMatchingVariant(p.variants, size);
            if (!variant || !variant.available) {
                p.el.style.display = 'none';
                p.el.classList.add('disabled');
                p.el.classList.remove('selected');
                // Remove from selection if it was selected
                cbSelectedItems = cbSelectedItems.filter(item => item.productEl !== p.el);
            } else {
                p.el.style.display = 'block';
                p.el.classList.remove('disabled');
                // If product is already selected, update its variant ID & price for this size
                const selectedItem = cbSelectedItems.find(item => item.productEl === p.el);
                if (selectedItem) {
                    selectedItem.variantId = variant.id;
                    selectedItem.price = variant.price;
                }
            }
        });

        // Hide empty collections
        const collectionContainers = document.querySelectorAll('.js-cb-collection-container');
        collectionContainers.forEach(container => {
            // Find products that don't have display: none
            const visibleProducts = Array.from(container.querySelectorAll('.js-cb-product-card')).filter(card => card.style.display !== 'none');
            if (visibleProducts.length === 0) {
                container.style.display = 'none';
            } else {
                container.style.display = 'block';
            }
        });

        // Trigger scroll event and dot rebuilds on all custom carousels
        document.querySelectorAll('.js-cb-collection-container').forEach(container => {
            container.dispatchEvent(new Event('cb-rebuild-dots'));
            const carousel = container.querySelector('.js-cb-carousel');
            if (carousel) carousel.dispatchEvent(new Event('scroll'));
        });
    };

    // (Size click logic handled during pill creation)

    cbProducts.forEach(p => {
        p.addEventListener('click', () => {
            if (!cbCurrentSize || p.classList.contains('disabled')) return;

            const isSelected = p.classList.contains('selected');
            const productData = cbParsedProducts.find(data => data.el === p);
            const variant = findMatchingVariant(productData.variants, cbCurrentSize);

            if (!variant || !variant.available) return;

            if (isSelected) {
                p.classList.remove('selected');
                cbSelectedItems = cbSelectedItems.filter(item => item.productEl !== p);
            } else {
                if (cbSelectedItems.length >= cbMax) {
                    alert(`You can only select up to ${cbMax} items.`);
                    return;
                }
                p.classList.add('selected');
                cbSelectedItems.push({
                    productEl: p,
                    variantId: variant.id,
                    price: variant.price
                });
            }
            updateCustomSummary();
        });
    });

    cbAddBtn.addEventListener('click', async () => {
        if (cbAddBtn.disabled) return;

        const uniqueBundleId = 'CB-' + generateUUID();
        const size = cbCurrentSize;

        const items = cbSelectedItems.map(item => ({
            id: item.variantId,
            quantity: 1,
            properties: {
                '_bundle_id': uniqueBundleId,
                '_bundle_title': cbTitle,
                '_bundle_type': 'custom',
                '_bundle_discount_code': cbDiscount || '',
                '_bundle_size': size,
                '_bundle_item_count': cbSelectedItems.length.toString()
            }
        }));

        cbAddBtn.disabled = true;
        const origText = cbAddBtn.innerHTML;
        cbAddBtn.innerHTML = 'Adding...';

        try {
            const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });

            if (res.ok) {
                cbAddBtn.innerHTML = 'Added!';
                setTimeout(() => { cbAddBtn.innerHTML = origText; cbAddBtn.disabled = false; }, 2000);

                // Clear selection
                cbSelectedItems = [];
                cbProducts.forEach(p => p.classList.remove('selected'));
                updateCustomSummary();

                if (cbDiscount && window.applyDiscount) {
                    await window.applyDiscount(cbDiscount);
                }
                if (window.openCartDrawer) {
                    window.openCartDrawer();
                }
            } else {
                const err = await res.json();
                alert(err.description || 'Error adding to cart');
                cbAddBtn.innerHTML = origText;
                cbAddBtn.disabled = false;
            }
        } catch (e) {
            console.error(e);
            cbAddBtn.innerHTML = origText;
            cbAddBtn.disabled = false;
        }
    });

});
